import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Shared notifications-unread-count store.
 *
 * Why this exists:
 * Two surfaces show the unread-count badge — the desktop Sidebar and the
 * mobile-header NotificationBell. Without a shared store, they each polled
 * independently and could disagree for up to 30 seconds after a mutation
 * (mark-all-read, dismiss, etc.). Worse, the /notifications page itself
 * mutates the server and had no way to tell the two badges to re-sync —
 * a confirmed regression caught by the testing agent.
 *
 * The context exposes:
 *  - `unreadCount`  — the badge number (capped at 99)
 *  - `refresh()`    — force an immediate re-fetch (call after any
 *                     mark-read / dismiss / etc.)
 *
 * Internally we keep the same 30-second polling cadence + route-change
 * refresh, but every consumer reads from the SAME state.
 */
const NotificationsContext = createContext({ unreadCount: 0, refresh: async () => {} });

export function NotificationsProvider({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const [unreadCount, setUnreadCount] = useState(0);

    const refresh = useCallback(async () => {
        if (!user) {
            setUnreadCount(0);
            return;
        }
        try {
            const { data } = await api.get("/notifications/unread-count");
            setUnreadCount(data?.count || 0);
        } catch (_e) {
            // Network blip — keep last known value rather than flashing 0.
        }
    }, [user]);

    // Initial + 30s poll. Cheap (1 API call per minute even at 60k DAU is
    // 1k RPS — fine on Grow Tier).
    useEffect(() => {
        refresh();
        if (!user) return undefined;
        const t = setInterval(refresh, 30_000);
        return () => clearInterval(t);
    }, [refresh, user]);

    // Refresh on route change so e.g. clicking a notification to navigate
    // away from /notifications immediately reflects the new state in both
    // the sidebar and the mobile-header bell.
    useEffect(() => { refresh(); }, [location.pathname, refresh]);

    return (
        <NotificationsContext.Provider value={{ unreadCount, refresh }}>
            {children}
        </NotificationsContext.Provider>
    );
}

export function useNotifications() {
    return useContext(NotificationsContext);
}
